import { useDispatch, useSelector } from "react-redux";
import {
  ADDON_MODAL_CONSTANTS,
  BUNDLE_ADDON_MODAL_STEP,
  MAX_BUNDLE_SIZE,
  PURCHASE_ADDON_MODAL_STEP
} from "rivals/components/shared/AddonOptions/constants";
import RivalsButton from "rivals/components/shared/Button";
import CheckMark from "rivals/components/shared/Icons/CheckMark";
import {
  addAddOnToBundle,
  setActiveAddOn,
  setModalStep,
  setUpsellStepOption
} from "rivals/redux/subscriptionAddons/actions";
import { getUpsellSelectedOption } from "rivals/redux/subscriptionAddons/selectors";
import { setData } from "rivals/shared/utils/qa";
import PromoCard from "./PromoCard";
import styles from "./index.module.scss";
import strings from "./strings";
import { UpsellFunnelStepType } from "./types";

const CURRENCY_DECIMAL = 2;

const { ALL_ACCESS, BUNDLE, STANDARD } = ADDON_MODAL_CONSTANTS;

const { allAccessRadioLabel } = strings;

const UpsellFunnelStep = ({
  addOn,
  addOns,
  closeButtonClick,
  parentPhilter
}: UpsellFunnelStepType): JSX.Element => {
  const dispatch = useDispatch();
  const selectedOption = useSelector(getUpsellSelectedOption);
  const allAccessAddOn =
    addOns && addOns.length && addOns.find(addOn => addOn.type === ALL_ACCESS);
  const bundleAddOn =
    addOns && addOns.length && addOns.find(addOn => addOn.type === BUNDLE);

  const funnelSteps = [
    {
      footer: (
        <div>{allAccessAddOn ? allAccessAddOn.formattedPriceString : ""}</div>
      ),
      hasFancyBackground: true,
      hidden: !allAccessAddOn,
      info: (
        <>
          <div>
            <span>
              <CheckMark />
            </span>
            <span>{strings.allAccessBulletOne}</span>
          </div>
          <div>
            <span>
              <CheckMark />
            </span>
            <span>{strings.allAccessBulletTwo}</span>
          </div>
        </>
      ),
      opt: ALL_ACCESS,
      radioLabel: (
        <div>
          {allAccessRadioLabel(
            allAccessAddOn ? allAccessAddOn.formattedPriceString : ""
          )}
        </div>
      ),
      sectionPromo: strings.allAccessUpsellPromoText
    },
    {
      cardPromo: strings.bundleUpsellPromo,
      footer: <div>{bundleAddOn ? bundleAddOn.formattedPriceString : ""}</div>,
      hidden:
        !bundleAddOn ||
        (bundleAddOn &&
          bundleAddOn.associatedSubscription &&
          bundleAddOn.associatedSubscription.subscribed),
      info: (
        <div>
          <span>
            <CheckMark />
          </span>
          <span>
            {strings.bundleBulletPrefix(
              bundleAddOn && bundleAddOn.price
                ? (parseFloat(bundleAddOn.price) / MAX_BUNDLE_SIZE).toFixed(
                    CURRENCY_DECIMAL
                  )
                : ""
            )}
          </span>
        </div>
      ),
      opt: BUNDLE,
      radioLabel: (
        <div>
          {strings.bundleRadioLabelPrefix}
          {addOn.siteLogoUrl ? (
            <img
              alt="site logo"
              src={addOn.siteLogoUrl}
              style={{ backgroundColor: addOn.siteColor }}
            />
          ) : (
            " "
          )}
          {strings.bundleRadioLabelSuffix}
        </div>
      )
    },
    {
      footer: <div>{addOn ? addOn.formattedPriceString : ""}</div>,
      opt: STANDARD,
      radioLabel: (
        <div>
          {strings.standardRadioLabel}
          {addOn.siteName}
          {addOn.siteLogoUrl && (
            <img
              alt="site logo"
              src={addOn.siteLogoUrl}
              style={{ backgroundColor: addOn.siteColor }}
            />
          )}
        </div>
      )
    }
  ];

  const onChangeSelection = (opt: string): void => {
    dispatch(setUpsellStepOption(opt));
  };

  const onNextClick = (opt?: string): void => {
    if (opt) {
      if (opt === ALL_ACCESS) {
        if (allAccessAddOn) {
          dispatch(setActiveAddOn(allAccessAddOn));
          dispatch(setModalStep(PURCHASE_ADDON_MODAL_STEP));
        }
      } else if (opt === BUNDLE) {
        if (bundleAddOn) {
          dispatch(setActiveAddOn(bundleAddOn));
          dispatch(setModalStep(BUNDLE_ADDON_MODAL_STEP));
          dispatch(addAddOnToBundle(addOn));
        }
      } else {
        dispatch(setModalStep(PURCHASE_ADDON_MODAL_STEP));
      }
    }
  };

  const onCancel = (): void => {
    closeButtonClick(true);
  };

  return (
    <div
      className={styles.modalBody}
      {...setData(parentPhilter, "upsell-funnel-step")}
    >
      <div
        className={styles.modalTitle}
        {...setData(parentPhilter, "upsell-funnel-step-title")}
      >
        {strings.headerText}
      </div>
      {funnelSteps.map(
        ({ hidden, ...option }, index) =>
          !hidden && (
            <PromoCard
              key={index}
              onClick={onChangeSelection}
              selectedPromo={selectedOption}
              {...option}
            />
          )
      )}
      <div className={styles.buttons}>
        <RivalsButton
          buttonText="Next"
          className={styles.submitButton}
          disabled={false}
          onClick={(): void => onNextClick(selectedOption)}
          size="small"
          {...setData(parentPhilter, "upsell-funnel-step-next-button")}
        />
        <RivalsButton
          buttonText="Cancel"
          className={styles.cancelButton}
          disabled={false}
          inverted
          onClick={onCancel}
          size="small"
          {...setData(parentPhilter, "upsell-funnel-step-cancel-button")}
        />
      </div>
    </div>
  );
};

export default UpsellFunnelStep;
